import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  callAgentTool, deleteHistoryItem, getConversation, getHistory, getLatestConversation,
  queryStream, saveConversation, subjectQueryStream, submitFeedback, toggleBookmark,
} from '../../../services/ai-explorer/aiExplorerService';
import { addWidget, createDashboard, getDashboards } from '../../../services/ai-explorer/dashboardService';
import { humanizeAgentDisplayData, humanizeAgentResponse } from '../../../utils/agentResponse';
import {
  AGENT_SURFACE, DEFAULT_WINDOW, TIME_WINDOWS, WINDOW_STORAGE_KEY,
  applyToolEvent, conversationTitle, errorKey, fromStoredMessages, historyForModel, isAgentHistoryItem, noticeMessage,
  parseSlashCommand, pendingAgentMessage, readToolPrefs, setApproval, tableToCsv, toStoredMessages, transcriptMarkdown,
  userMessage, windowParam, writeToolPrefs,
} from './agentModel';

const HISTORY_PAGE = 30;

const readWindow = () => {
  try {
    const v = localStorage.getItem(WINDOW_STORAGE_KEY);
    return TIME_WINDOWS.some((w) => w.id === v) ? v : DEFAULT_WINDOW;
  } catch { return DEFAULT_WINDOW; }
};

export const download = (name, text, type) => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = name; a.style.display = 'none';
  document.body.appendChild(a); a.click();
  requestAnimationFrame(() => { a.remove(); URL.revokeObjectURL(url); });
};

/**
 * One Saf3AI Agent conversation, shared by the full page and the slide-in
 * panel so both read and write the same history.
 *
 * `getPageContext()` returns what the backend should know about where the
 * question was asked (route, page, summary…); the time window is added here.
 * `enabled` defers the history/insight loads until a panel is actually open.
 */
export default function useAgentConversation({ t, getPageContext, enabled = true }) {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [title, setTitle] = useState('');
  const [bookmarked, setBookmarked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [windowId, setWindowId] = useState(readWindow);
  const [toolPrefs, setToolPrefs] = useState(readToolPrefs);
  const toolPrefsRef = useRef(toolPrefs); toolPrefsRef.current = toolPrefs;
  const [toasts, setToasts] = useState([]);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [history, setHistory] = useState({ items: [], total: 0, page: 0, loading: false, error: false });
  const [historyQuery, setHistoryQuery] = useState('');
  const [latest, setLatest] = useState(null);
  const [dashboards, setDashboards] = useState({ list: [], loading: false, error: false });

  const abortRef = useRef(null);
  const textareaRef = useRef(null);
  const searchRef = useRef(null);
  const bodyRef = useRef(null);
  const messagesRef = useRef(messages); messagesRef.current = messages;
  const titleRef = useRef(title); titleRef.current = title;
  const conversationIdRef = useRef(conversationId); conversationIdRef.current = conversationId;
  const busyRef = useRef(busy); busyRef.current = busy;
  const getPageContextRef = useRef(getPageContext); getPageContextRef.current = getPageContext;

  /** A short message; `action` ({label, onClick}) adds a button such as Undo and keeps it up longer. */
  const toast = useCallback((text, kind = 'ok', action = null) => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts, { id, text, kind, action }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), action ? 6000 : 3200);
  }, []);
  const dismissToast = useCallback((id) => setToasts((ts) => ts.filter((x) => x.id !== id)), []);

  const scrollToEnd = useCallback(() => {
    const el = bodyRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, []);

  // ── history ─────────────────────────────────────────────────────
  const loadHistory = useCallback(async (page = 1) => {
    setHistory((h) => ({ ...h, loading: true, error: false }));
    try {
      const res = await getHistory({ page, per_page: HISTORY_PAGE });
      const items = (res.data?.items || []).filter(isAgentHistoryItem);
      setHistory((h) => ({
        items: page === 1 ? items : [...h.items, ...items],
        total: typeof res.data?.total === 'number' ? res.data.total : items.length,
        page, loading: false, error: false,
      }));
    } catch {
      setHistory((h) => ({ ...h, loading: false, error: true }));
    }
  }, []);

  useEffect(() => { if (enabled) loadHistory(1); }, [enabled, loadHistory]);
  useEffect(() => {
    if (!enabled) return;
    getLatestConversation({ surface: AGENT_SURFACE })
      .then((res) => { const c = res.data?.conversation; if (c?.id && Array.isArray(c.messages) && c.messages.length) setLatest(c); })
      .catch(() => setLatest(null));
  }, [enabled]);

  const changeToolPrefs = useCallback((patch) => {
    setToolPrefs((p) => { const next = { ...p, ...patch }; writeToolPrefs(next); return next; });
  }, []);

  const changeWindow = useCallback((id) => {
    setWindowId(id);
    try { localStorage.setItem(WINDOW_STORAGE_KEY, id); } catch { /* private mode */ }
  }, []);

  // ── persistence ─────────────────────────────────────────────────
  const persist = useCallback(async (msgs, overrides = {}) => {
    const stored = toStoredMessages(msgs);
    if (!stored.some((m) => m.role === 'user')) return conversationIdRef.current;
    const nextTitle = overrides.title ?? titleRef.current ?? '';
    try {
      const res = await saveConversation({
        conversation_id: conversationIdRef.current || undefined,
        surface: AGENT_SURFACE,
        title: nextTitle || conversationTitle(stored, t('agent.untitled')),
        messages: stored,
      });
      const id = res.data?.conversation_id || conversationIdRef.current;
      if (id && id !== conversationIdRef.current) { setConversationId(id); conversationIdRef.current = id; }
      if (!nextTitle) setTitle(conversationTitle(stored, t('agent.untitled')));
      loadHistory(1);
      return id;
    } catch (err) {
      if (conversationIdRef.current && err?.response?.status === 404) {
        setConversationId(null);
        conversationIdRef.current = null;
        return persist(msgs, overrides);
      }
      toast(t('agent.saveFailed'), 'error');
      return conversationIdRef.current;
    }
  }, [loadHistory, t, toast]);

  // ── asking ──────────────────────────────────────────────────────
  /**
   * Ask a question. `base` replaces the thread it is appended to (regenerate);
   * `subject` is one record the question is about, sent through the
   * subject endpoint so the model does not have to guess what "this" is.
   */
  const ask = useCallback(async (question, { base, subject } = {}) => {
    const q = String(question || '').trim();
    if (!q || busyRef.current) return;
    const pending = pendingAgentMessage();
    const start = [...(base || messagesRef.current), userMessage(q)];
    setMessages([...start, pending]);
    setInput('');
    setBusy(true); busyRef.current = true;
    scrollToEnd();

    const controller = new AbortController();
    abortRef.current = controller;
    let text = '';
    const patch = (fn) => setMessages((ms) => ms.map((m) => (m.id === pending.id ? fn(m) : m)));
    const onToken = (token) => { text += token; patch((m) => ({ ...m, content: humanizeAgentResponse(text), loading: false, streaming: true })); scrollToEnd(); };
    const onComplete = (response) => {
      if (!response || typeof response !== 'object') return;
      const answer = humanizeAgentResponse(response.answer || text || '');
      patch((m) => ({
        ...m, content: answer, loading: false, streaming: false,
        displayType: response.display_type || 'text', chartType: response.chart_type || null,
        data: humanizeAgentDisplayData(response.data) || null,
        ...(Array.isArray(response.suggested_prompts) ? { suggestions: response.suggested_prompts.map(humanizeAgentResponse) } : {}),
      }));
    };
    const pageContext = { ...(getPageContextRef.current?.() || {}), time_range: windowParam(windowId) };
    const conversationHistory = historyForModel(start.slice(0, -1));

    try {
      if (subject) {
        await subjectQueryStream(q, subject, {
          onToken, onComplete, conversationHistory, includePlatformData: true,
          onError: (err) => { throw err; },
        });
      } else {
        await queryStream(
          q, onToken,
          (suggestions) => patch((m) => ({ ...m, suggestions: (suggestions || []).map(humanizeAgentResponse) })),
          () => patch((m) => ({ ...m, loading: false, streaming: false })),
          onComplete,
          (data) => patch((m) => ({
            ...m, loading: false, streaming: false,
            content: m.content || t('agent.dashboardCreatedText', { title: data.dashboard_title || '' }),
            dashboard: { id: data.dashboard_id, title: data.dashboard_title || t('agent.newDashboard'), count: data.widgets_generated || 0 },
          })),
          {
            signal: controller.signal, page_context: pageContext, conversation_history: conversationHistory,
            use_tools: toolPrefsRef.current.useTools, tools: { exclude: toolPrefsRef.current.exclude },
            onEvent: (ev) => { patch((m) => applyToolEvent(m, ev)); scrollToEnd(); },
          },
        );
      }
    } catch (err) {
      if (err?.name === 'AbortError') patch((m) => ({ ...m, loading: false, streaming: false, stopped: true }));
      else patch((m) => ({ ...m, loading: false, streaming: false, error: true, errorKey: errorKey(err), content: '' }));
    } finally {
      abortRef.current = null;
      setBusy(false); busyRef.current = false;
      setMessages((ms) => {
        const final = ms.map((m) => (m.id === pending.id ? { ...m, loading: false, streaming: false } : m))
          .filter((m) => !(m.id === pending.id && !m.content && !m.error && !m.stopped));
        persist(final);
        return final;
      });
      scrollToEnd();
    }
  }, [persist, scrollToEnd, t, windowId]);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const newChat = useCallback(() => {
    stop();
    setMessages([]); setConversationId(null); conversationIdRef.current = null;
    setTitle(''); setBookmarked(false); setInput('');
    textareaRef.current?.focus();
  }, [stop]);

  /** Show a page notice (greeting, "you're now on X") without saving it. */
  const addNotice = useCallback((content, contextCards) => {
    setMessages((ms) => [...ms, noticeMessage(content, contextCards)]);
    scrollToEnd();
  }, [scrollToEnd]);

  /** Replace the latest notice's cards once a page's summary arrives. */
  const patchLastNotice = useCallback((content, contextCards) => {
    setMessages((ms) => {
      const idx = [...ms].reverse().findIndex((m) => m.ephemeral);
      if (idx < 0) return ms;
      const i = ms.length - 1 - idx;
      if (ms[i].contextCards?.length) return ms;
      return ms.map((m, n) => (n === i ? { ...m, content, contextCards: contextCards.length ? contextCards : undefined } : m));
    });
  }, []);

  const exportTranscript = useCallback(() => {
    const msgs = messagesRef.current;
    if (!msgs.some((m) => m.role === 'user')) { toast(t('agent.nothingToExport'), 'error'); return; }
    const md = transcriptMarkdown(msgs, { title: titleRef.current || undefined, agentLabel: t('agent.title'), userLabel: t('agent.you') });
    download(`saf3ai-agent-${new Date().toISOString().slice(0, 10)}.md`, md, 'text/markdown');
  }, [t, toast]);

  const togglePin = useCallback(async (item) => {
    const target = item || (conversationIdRef.current ? { id: conversationIdRef.current, bookmarked } : null);
    if (!target) { toast(t('agent.nothingToPin'), 'error'); return; }
    try {
      const res = await toggleBookmark(target.id, !target.bookmarked);
      const next = !!res.data?.bookmarked;
      if (target.id === conversationIdRef.current) setBookmarked(next);
      setHistory((h) => ({ ...h, items: h.items.map((i) => (i.id === target.id ? { ...i, bookmarked: next } : i)) }));
    } catch { toast(t('agent.pinFailed'), 'error'); }
  }, [bookmarked, t, toast]);

  const runCommand = useCallback(({ command, arg }) => {
    switch (command.run) {
      case 'newChat': newChat(); break;
      case 'exportTranscript': exportTranscript(); break;
      case 'togglePin': togglePin(null); break;
      case 'showShortcuts': setShortcutsOpen(true); break;
      case 'setWindow':
        if (TIME_WINDOWS.some((w) => w.id === arg)) changeWindow(arg);
        else toast(t('agent.windowUsage', { windows: TIME_WINDOWS.map((w) => w.id).join(', ') }), 'error');
        break;
      default: break;
    }
  }, [changeWindow, exportTranscript, newChat, t, toast, togglePin]);

  const send = useCallback(() => {
    const parsed = parseSlashCommand(input);
    if (parsed) { setInput(''); runCommand(parsed); return; }
    if (input.trim().startsWith('/')) { toast(t('agent.unknownCommand'), 'error'); return; }
    ask(input);
  }, [ask, input, runCommand, t, toast]);

  const editLast = useCallback(() => {
    const last = [...messagesRef.current].reverse().find((m) => m.role === 'user');
    if (last) setInput(last.content);
  }, []);

  const regenerate = useCallback((m) => {
    const ms = messagesRef.current;
    const idx = ms.findIndex((x) => x.id === m.id);
    const lastUser = [...ms.slice(0, idx < 0 ? ms.length : idx)].reverse().find((x) => x.role === 'user');
    if (!lastUser) return;
    ask(lastUser.content, { base: ms.slice(0, ms.indexOf(lastUser)) });
  }, [ask]);

  // ── history actions ─────────────────────────────────────────────
  const openConversation = useCallback(async (item) => {
    if (busyRef.current) stop();
    try {
      const res = await getConversation(item.id);
      const doc = res.data || {};
      setMessages(fromStoredMessages(doc.messages));
      setConversationId(item.id); conversationIdRef.current = item.id;
      setTitle(doc.title || item.title || '');
      setBookmarked(!!doc.bookmarked);
      setLatest(null);
      scrollToEnd();
      return true;
    } catch { toast(t('agent.loadFailed'), 'error'); return false; }
  }, [scrollToEnd, stop, t, toast]);

  const deleteConversation = useCallback(async (item) => {
    try {
      await deleteHistoryItem(item.id);
      setHistory((h) => ({ ...h, items: h.items.filter((i) => i.id !== item.id), total: Math.max(0, h.total - 1) }));
      if (item.id === conversationIdRef.current) newChat();
    } catch { toast(t('agent.deleteFailed'), 'error'); }
  }, [newChat, t, toast]);

  const rename = useCallback(async (next) => {
    const clean = String(next || '').trim();
    if (!clean || clean === titleRef.current) return;
    setTitle(clean);
    const id = await persist(messagesRef.current, { title: clean });
    if (id) setHistory((h) => ({ ...h, items: h.items.map((i) => (i.id === id ? { ...i, title: clean } : i)) }));
  }, [persist]);

  /** Make sure the thread is saved and return its id (for a deep link). */
  const ensureSaved = useCallback(() => persist(messagesRef.current), [persist]);

  // ── message actions ─────────────────────────────────────────────
  const loadDashboards = useCallback(() => {
    setDashboards((d) => ({ ...d, loading: true, error: false }));
    getDashboards()
      .then((res) => setDashboards({ list: res.data?.items || [], loading: false, error: false }))
      .catch(() => setDashboards({ list: [], loading: false, error: true }));
  }, []);

  /**
   * The turns before an answer's question (oldest first), so a pinned follow-up
   * can be made self-contained. Empty for the first question of a chat.
   */
  const contextBefore = (m, turns = 6) => {
    const ms = messagesRef.current;
    const idx = ms.findIndex((x) => x.id === m.id);
    const before = ms.slice(0, idx < 0 ? ms.length : idx);
    const q = before.map((x) => x.role).lastIndexOf('user');
    return before.slice(0, q < 0 ? before.length : q)
      .filter((x) => !x.ephemeral && x.content && !x.error)
      .slice(-turns)
      .map((x) => ({ role: x.role === 'user' ? 'user' : 'assistant', content: String(x.content).slice(0, 600) }));
  };

  const questionBefore = (m) => {
    const ms = messagesRef.current;
    const idx = ms.findIndex((x) => x.id === m.id);
    return [...ms.slice(0, idx)].reverse().find((x) => x.role === 'user')?.content || '';
  };

  const actions = useMemo(() => ({
    copy: async (m) => {
      try { await navigator.clipboard.writeText(m.content); toast(t('agent.copied')); } catch { toast(t('agent.copyFailed'), 'error'); }
    },
    feedback: async (m, type, comment) => {
      try {
        await submitFeedback({
          conversation_id: conversationIdRef.current, query: questionBefore(m), response: String(m.content).slice(0, 20000),
          feedback_type: type, comment: comment || undefined, display_type: m.displayType || 'text',
        });
        toast(t('agent.feedbackThanks'));
        return true;
      } catch { toast(t('agent.feedbackFailed'), 'error'); return false; }
    },
    regenerate,
    followUp: (text) => ask(text),
    // A write the agent wants to make. It runs only here, after the user said yes.
    approve: async (m, approval) => {
      const update = (p) => setMessages((ms) => ms.map((x) => (x.id === m.id ? setApproval(x, approval.id, p) : x)));
      // The card may hand back edited arguments (the alert rule editor): keep what actually ran.
      update({ status: 'running', arguments: approval.arguments });
      try {
        const res = await callAgentTool({ name: approval.name, arguments: approval.arguments, conversation_id: conversationIdRef.current });
        const ok = !!res.data?.ok;
        update({ status: ok ? 'approved' : 'failed', result: res.data });
        toast(ok ? t('agent.actionRan', { name: approval.name }) : t('agent.actionFailed', { name: approval.name }), ok ? 'ok' : 'error');
        setMessages((ms) => { persist(ms); return ms; });
      } catch (err) {
        update({ status: 'failed', result: { ok: false, error: err?.response?.data?.detail || err?.message || '' } });
        toast(t('agent.actionFailed', { name: approval.name }), 'error');
      }
    },
    reject: (m, approval) => setMessages((ms) => ms.map((x) => (x.id === m.id ? setApproval(x, approval.id, { status: 'rejected' }) : x))),
    // A write the card made itself, in the person's own session (an API key, whose secret must not go
    // through the tool call or be stored): record what happened, without the secret, and keep it.
    resolve: (m, approval, patch) => {
      setMessages((ms) => {
        const next = ms.map((x) => (x.id === m.id ? setApproval(x, approval.id, patch) : x));
        persist(next);
        return next;
      });
    },
    downloadCsv: (table) => download(`saf3ai-agent-table-${Date.now()}.csv`, tableToCsv(table), 'text/csv'),
    saveWidget: async (m, dashboardId) => {
      const question = questionBefore(m);
      try {
        let id = dashboardId;
        let name = dashboards.list.find((d) => d.id === id)?.title;
        if (!id) {
          const created = await createDashboard({ title: question.slice(0, 60) || t('agent.newDashboard') });
          id = created.data?.id; name = created.data?.title || question.slice(0, 60);
        }
        await addWidget(id, {
          title: question.slice(0, 80) || t('agent.untitled'), query: question || t('agent.untitled'),
          display_type: m.displayType || 'text', chart_type: m.chartType || null,
          data: m.data || { text: String(m.content).slice(0, 10000) }, size: 'medium',
        });
        toast(t('agent.widgetSaved', { title: name || '' }));
      } catch { toast(t('agent.saveWidgetFailed'), 'error'); }
    },
  }), [ask, dashboards.list, persist, regenerate, t, toast]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return {
    messages, setMessages, conversationId, title, bookmarked, busy, input, setInput,
    windowId, changeWindow, toolPrefs, changeToolPrefs, toasts, toast, dismissToast, shortcutsOpen, setShortcutsOpen,
    history, historyQuery, setHistoryQuery, loadHistory, latest, setLatest,
    dashboards: { ...dashboards, load: loadDashboards }, actions,
    ask, stop, send, newChat, editLast, regenerate, exportTranscript, togglePin, runCommand,
    openConversation, deleteConversation, rename, ensureSaved, addNotice, patchLastNotice,
    questionFor: questionBefore,
    contextFor: contextBefore,
    pageContextNow: () => getPageContextRef.current?.() || null,
    textareaRef, searchRef, bodyRef, scrollToEnd,
  };
}
